<template>
  <CardSection :loading="isSavableStateLoading"
    :error="majorError"
    header-class="title-bar">
    <template #header>
      Edit Notification Settings - Robocall Reduction
    </template>

    <template #body>
    <SubSection :sub-section-heading="'Alerts'" :sub-section-nav="subSectionNav">
      <template #body>
        <div class="alert alert-thin alert-info col-sm-12 mt-sm-1 mt-md-0" style="margin-bottom: 0;" v-if="robocallAlerts.config.last_email_notification_sent_at">
          <font-awesome-icon :icon="['fas', 'circle-info']" />
          Last email alert sent at {{formatDateTimeET(robocallAlerts.config.last_email_notification_sent_at)}} ET
        </div>
        <br>
        <div class="tab-back">
          <SubSection>
            <template #body>
              <VeeForm @submit="submitNewRobocallNotificationSettings">
                <TextInput v-model="robocallAlerts.config.email_alert_threshold"
                  name="email_alert_threshold"
                  label="Offer-email Outage Alert Threshold (minutes)"
                  type="number"
                  rules="required|min_value:1"
                  col-style="form-group-standard-column" 
                />

                <div v-if="robocallAlerts.config.email_alert_threshold > 20" class="alert alert-warning alert-thin col-sm-12 mt-sm-1 mt-md-0 ml-2 mr-2" >
                  Note that this leaves less than 10 minutes for reacting to email outages. Consider a delay setting of less than 20 minutes.
                </div>

                <TextInput v-model="robocallAlerts.config.call_alert_threshold"
                  name="call_alert_threshold"
                  label="Offer-call Outage Alert Threshold (minutes)"
                  type="number"
                  rules="required|min_value:1"
                  col-style="form-group-standard-column" 
                />

                <div v-if="robocallAlerts.config.call_alert_threshold <= robocallAlerts.config.email_alert_threshold" class="alert alert-warning alert-thin col-sm-12 mt-sm-1 mt-md-0 ml-2 mr-2" >
                  Consider setting 'Offer-call Outage Alert Threshold' to be higher than 'Offer-email Outage Alert Threshold' to prevent multiple alerts being sent simultaneously.
                </div>

                <div class="inline-row">
                  <ActionToolbar :component-state="thresholdState"
                    :error-message="thresholdError"
                    success-message="Successfully updated notification settings!"
                    primary-button-text="Change Alert Threshold(s)"
                    :secondaryEnabled="false">
                  </ActionToolbar>
                </div>
              </VeeForm>
              
            </template>
          </SubSection>

          <hr class="inline-row">

          <SubSection>
            <template #body>

              <VeeForm @submit="submitNewRobocallAlertEmails">
                <TextInput v-model="alertEmail"
                  name="alert_email"
                  label="Alert Email"
                  rules="email|required"
                  col-style="form-group-standard-column" 
                />

                <div class="inline-row">
                  <ActionToolbar :component-state="alertEmailState"
                    :error-message="alertEmailError"
                    success-message="Successfully updated notification settings!"
                    primary-button-text="Save Email"
                    :secondaryEnabled="false">
                  </ActionToolbar>
                </div>
              </VeeForm>
            </template>
          </SubSection>
        </div>

        <PortalTable
          ref="robocall_alert_emails_index"
          table-id="robocall_alert_emails_index"
          :loading="isSavableStateProcessing"
          :table-data="records"
          :paginator=false
          mode="local"
          empty="No users found!"
        >
          <template #columns>
            <Column field="email" header="Email" style="min-width: 100px;" />
            <Column header="Updated At" style="min-width: 100px;">
              <template #body="slotProps">
                {{formatDateTimeWithSecondsET(slotProps.data.updated_at)}} ET
              </template>
            </Column>
            <Column field="updated_by" header="Updated By" style="min-width: 100px;"/>
            <Column header="Status" style="min-width: 100px;">
              <template #body="slotProps">
                {{ slotProps.data.status == true ? 'On' : 'Off' }}
              </template>
            </Column>
            <Column header="Actions" style="min-width: 100px;">
              <template #body="slotProps">
                <a href="#" :title="'Turn Off Notifications'" class="action btn btn-fill btn-outline-primary mx-2" 
                  @click.prevent="toggleRobocallNotificationsForEmail(slotProps.data.email)"
                  v-if="slotProps.data.status == true"
                >
                  <font-awesome-icon class="desktop-action" :icon="['fas', 'circle-minus']" />
                  <span class="mobile-action">Turn Off Notifications</span>
                </a>
                <a href="#" :title="'Turn On Notifications'" class="action btn btn-fill btn-outline-primary mx-2" 
                  @click.prevent="toggleRobocallNotificationsForEmail(slotProps.data.email)"
                  v-else
                >
                  <font-awesome-icon class="desktop-action" :icon="['fas', 'circle-check']" />
                  <span class="mobile-action">Turn On Notifications</span>
                </a>

                <a href="#" title="Remove User" class="action btn btn-fill btn-outline-danger" @click.prevent="removeEmailFromRobocallNotificationSettings(slotProps.data.email)">
                  <font-awesome-icon class="desktop-action" :icon="['far', 'circle-xmark']" />
                  <span class="mobile-action">Remove User</span>
                </a>
              </template>
            </Column>
          </template>
        </PortalTable>
      </template>
    </SubSection>
    </template>
  </CardSection>
</template>
<script>
import CardSection from "@/src/components/shared/CardSection.vue";
import SubSection from "@/src/components/shared/SubSection.vue";
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
import { Form as VeeForm } from 'vee-validate';
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import { useRobocallReductionsStore } from '@/src/stores/robocall_reductions';
import { useUserStore } from '@/src/stores/user';
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { paginationMixin } from "@/src/mixins/paginationMixin";
import { formatDateTimeWithSecondsET, formatDateTimeET } from "@/src/date-helpers";
import {robocallReductionSubsectionNavMixin} from "@/src/components/admin-area/application-settings/robocall-reductions/robocallReductionSubsectionNavMixin";
import { pageMixin } from "@/src/mixins/pageMixin";

const DEFAULT_EMAIL_ALERT_THRESHOLD = 10;
const DEFAULT_CALL_ALERT_THRESHOLD = 20;

export default {
  components: {
    CardSection,
    SubSection,
    TextInput,
    VeeForm,
    ActionToolbar,
    PortalTable,
    Column
  },
  mixins: [ savableStateMixin, paginationMixin, robocallReductionSubsectionNavMixin, pageMixin ],
  data() {
    return {
      robocallAlerts: {
        id: null,
        config: {
          email_alert_threshold: DEFAULT_EMAIL_ALERT_THRESHOLD,
          call_alert_threshold: DEFAULT_CALL_ALERT_THRESHOLD,
          alertable_emails: [],
          last_email_notification_sent_at: null
        }
      },
      alertEmail: null,
      thresholdState: null,
      thresholdError: null,
      alertEmailState: null,
      alertEmailError: null,
      formatDateTimeWithSecondsET: formatDateTimeWithSecondsET,
      formatDateTimeET: formatDateTimeET
    }
  },

  setup() {
    const robocallReductionsStore = useRobocallReductionsStore();
    const userStore = useUserStore();

    return { robocallReductionsStore, userStore };
  },

  async mounted() {
    let url = window.location.pathname.split('/')
    this.type = url[url.length-(this.$route.params.id ? 3 : 2)]

    await this.buildRobocallAlertData();
  },

  methods: {
    // Get our robocall alert object
    async buildRobocallAlertData() {
      try {
        this.setSavableStateLoading();
        const response = await this.robocallReductionsStore.loadRobocallAlertData();

        this.setRobocallAlerts(response);
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },

    // Add new email to our config
    async submitNewRobocallNotificationSettings() {
      this.thresholdState = this.SaveStates.Processing

      const payload = {
        email_alert_threshold: this.robocallAlerts.config.email_alert_threshold,
        call_alert_threshold: this.robocallAlerts.config.call_alert_threshold
      }

      try {
        const response = await this.robocallReductionsStore.updateRobocallAlertSettings(payload)
        this.setRobocallAlerts(response)
        this.thresholdState = this.SaveStates.Success
      } catch (error) {
        this.thresholdError = error
        this.thresholdState = this.SaveStates.Errored
      }
    },

    async submitNewRobocallAlertEmails() {
      const payload = {
        email: this.alertEmail
      }
        
      this.alertEmailState = this.SaveStates.Processing
      try {
        const response = await this.robocallReductionsStore.updateRobocallAlertEmails(payload)
        this.setRobocallAlerts(response)
        this.alertEmailState = this.SaveStates.Success
      } catch (error) {
        this.alertEmailError = error
        this.alertEmailState = this.SaveStates.Errored
      }
    },

    // Set emails status to true/false on config
    async toggleRobocallNotificationsForEmail(email) {
      this.setSavableStateProcessing();
      try {
        const response = await this.robocallReductionsStore.changeAlertEmailStatus({ email: email });
        this.setRobocallAlerts(response);

        this.setSavableStateIdle();
      } catch (error) {
        this.handleMajorError(error);
      } 
    },

    // remove email from config
    async removeEmailFromRobocallNotificationSettings(email) {
      if(!confirm(`Are you sure you want to delete email: ${email} ?`)) {
        return;
      }
      this.setSavableStateProcessing();
      try {
        const response = await this.robocallReductionsStore.removeAlertEmail({ email: email });
        this.setRobocallAlerts(response);
        this.setSavableStateIdle();
      } catch (error) {
        this.handleMajorError(error);
      }
    },

    // Use our robocall alert config to set data useable by our table
    setRobocallAlerts(record) {
      this.alertEmail = null; 
      this.robocallAlerts.id = record?.id || null

      if (!this.robocallAlerts.config) {
        this.robocallAlerts.config = { email_alert_threshold: 20, emails: [] }
      }

      // Update alert thresholds reactively
      this.robocallAlerts.config.email_alert_threshold = Number(record?.config?.email_alert_threshold) || DEFAULT_EMAIL_ALERT_THRESHOLD
      this.robocallAlerts.config.call_alert_threshold = Number(record?.config?.call_alert_threshold) || DEFAULT_CALL_ALERT_THRESHOLD

      const emails = Array.isArray(record?.config?.alertable_emails) ? record.config.alertable_emails.map(e => ({
        email: e.email,
        status: e.status,
        updated_at: e.updated_at,
        updated_by: e.updated_by
      })) : []

      this.robocallAlerts.config.last_email_notification_sent_at = record.config.last_email_notification_sent_at
      this.robocallAlerts.config.alertable_emails = emails
      this.records = emails
    }
  }
}
</script>